import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';
import StateContainer from 'components/StateContainer';
import { getApplicationData } from 'selectors/getApplicationData';
import { ReactComponent as LoadingSpinner } from 'images/loader.svg';
import useDispatchWithUnwrap from 'hooks/useDispatchWithUnwrap';
import {
  authSessionThunk,
  getApplicationData as getApplicationDataThunk,
  preVerifyApplication,
  verifyApplication,
} from 'thunks';
import { FlowComponentType } from 'routes/types';
import { RoutePath } from 'enums/Routes';
import { useNavigate } from 'hooks/useNavigate';
import FormContainer from 'components/LoanForm/FormContainer';
import { getAuthData } from 'selectors/getAuthData';
import { infoNotification } from 'notifications';
import { OfferAvailableState } from 'components/DebtConsolidation/OfferAvailable';

const VerifyingApplication = ({ handleNext }: FlowComponentType) => {
  const dispatchWithUnwrap = useDispatchWithUnwrap();
  const navigate = useNavigate();

  const { application, paystubsUploaded } = useSelector(getApplicationData);
  const { id: applicationId } = application!;
  const verificationStepsProgress = application?.verificationStepProgress;

  const { sessionToken } = useSelector(getAuthData);

  useEffect(() => {
    (async () => {
      try {
        if (!paystubsUploaded && !verificationStepsProgress?.paystubs) {
          const preVerifyResult = await dispatchWithUnwrap(preVerifyApplication(applicationId));

          if (preVerifyResult?.paystubsRequired) return navigate(RoutePath.UploadIncomeVerification);
          if (preVerifyResult?.needsVerifyIncomeOption) return navigate(RoutePath.VerifyIncome);
        }

        // Method is best-effort at verification: tradelines/payoffs if available, never a flow blocker.
        if (sessionToken) {
          try {
            await dispatchWithUnwrap(authSessionThunk({ applicationId, sessionToken }));
          } catch {
            // Continue without Method entity (invalid zip, auth errors, outages, etc.).
          }
        }

        const { iovation_blackbox: iovationBlackbox } = window as any;

        if (!iovationBlackbox) {
          throw new Error('iovationBlackbox is undefined');
        }

        (window as any).nid('applicationSubmit', 'kycCheck');
        (window as any).nid('closeSession');

        const result = await dispatchWithUnwrap(
          verifyApplication({ applicationId, kycVariables: { iovationBlackbox } }),
        );
        analytics.track('Verify Result', result);

        if (result.underwritingPricingDifference) {
          infoNotification('Your offer has changed. Please select a new one.');
          await dispatchWithUnwrap(getApplicationDataThunk(applicationId));
          navigate(RoutePath.OfferAvailable, { state: { updateOffers: true } as OfferAvailableState });
          return;
        }

        handleNext(result.status);
      } catch (error) {
        handleNext();
      }
    })();
  }, []);

  return (
    <FormContainer>
      <StateContainer icon={<LoadingSpinner />} title="Verifying..." />
    </FormContainer>
  );
};

export default VerifyingApplication;
